import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PaymentIcon from '@mui/icons-material/Payment';
import { useAuth } from '../../contexts/AuthContext';
import { mainApi } from '../../config/apiClient';

type PaymentProvider = 'paytr' | 'parampos';

interface AddPaymentIntegrationModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const PROVIDER_OPTIONS: Array<{
  value: PaymentProvider;
  label: string;
  disabled: boolean;
  hint?: string;
}> = [
  { value: 'paytr', label: 'PayTR', disabled: false },
  { value: 'parampos', label: 'ParamPos', disabled: true, hint: 'Yakında' },
];

const AddPaymentIntegrationModal: React.FC<AddPaymentIntegrationModalProps> = ({
  open,
  onClose,
  onSaved,
}) => {
  const { branches } = useAuth();

  const [provider, setProvider] = useState<PaymentProvider>('paytr');
  const [branchId, setBranchId] = useState<string>(''); // boş => şirket geneli
  const [isEnabled, setIsEnabled] = useState<boolean>(true);
  const [isTestMode, setIsTestMode] = useState<boolean>(false);
  const [is3DEnabled, setIs3DEnabled] = useState<boolean>(true);

  // PayTR alanları
  const [merchantId, setMerchantId] = useState<string>('');
  const [merchantKey, setMerchantKey] = useState<string>('');
  const [merchantSalt, setMerchantSalt] = useState<string>('');
  const [showMerchantKey, setShowMerchantKey] = useState<boolean>(false);
  const [showMerchantSalt, setShowMerchantSalt] = useState<boolean>(false);

  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setProvider('paytr');
      setBranchId('');
      setIsEnabled(true);
      setIsTestMode(false);
      setIs3DEnabled(true);
      setMerchantId('');
      setMerchantKey('');
      setMerchantSalt('');
      setShowMerchantKey(false);
      setShowMerchantSalt(false);
      setSaving(false);
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    setError(null);

    if (provider === 'paytr') {
      if (!merchantId.trim()) {
        setError('Merchant ID zorunludur.');
        return;
      }
      if (!merchantKey.trim()) {
        setError('Merchant Key zorunludur.');
        return;
      }
      if (!merchantSalt.trim()) {
        setError('Merchant Salt zorunludur.');
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        Provider: provider,
        BranchId: branchId.trim() || null,
        IsEnabled: isEnabled,
        IsTestMode: isTestMode,
        Is3DEnabled: is3DEnabled,
        MerchantId: merchantId.trim() || null,
        MerchantKey: merchantKey.trim() || null,
        MerchantSalt: merchantSalt.trim() || null,
        ClientCode: null,
        ClientUsername: null,
        ClientPassword: null,
        Guid: null,
      };

      const response = await mainApi.post('/api/paymentproviderintegration/create', payload);
      const result = response.data;

      if (result.Success === false || result.success === false) {
        throw new Error(result.Message || result.message || 'Kayıt oluşturulamadı.');
      }

      onSaved();
      onClose();
    } catch (err: any) {
      const msg =
        err?.response?.data?.Message ||
        err?.response?.data?.message ||
        err?.message ||
        'Kayıt oluşturulurken bir hata oluştu.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Ödeme Entegrasyonu Ekle
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="provider-select-label">Sağlayıcı</InputLabel>
            <Select
              labelId="provider-select-label"
              value={provider}
              label="Sağlayıcı"
              onChange={(e) => setProvider(e.target.value as PaymentProvider)}
            >
              {PROVIDER_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {opt.label}
                    {opt.hint && (
                      <Chip label={opt.hint} size="small" color="warning" variant="outlined" />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="branch-select-label">Şube (boş bırakılırsa şirket geneli)</InputLabel>
            <Select
              labelId="branch-select-label"
              value={branchId}
              label="Şube (boş bırakılırsa şirket geneli)"
              onChange={(e) => setBranchId(e.target.value as string)}
            >
              <MenuItem value="">
                <em>Şirket Geneli (tüm şubeler)</em>
              </MenuItem>
              {branches.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider sx={{ my: 1 }}>
            <Chip label="Durum" size="small" />
          </Divider>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isEnabled}
                  onChange={(e) => setIsEnabled(e.target.checked)}
                  color="success"
                />
              }
              label="Aktif"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isTestMode}
                  onChange={(e) => setIsTestMode(e.target.checked)}
                  color="warning"
                />
              }
              label="Test Modu"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={is3DEnabled}
                  onChange={(e) => setIs3DEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label="3D Secure"
            />
          </Box>

          {provider === 'paytr' && (
            <>
              <Divider sx={{ my: 1 }}>
                <Chip label="PayTR Bilgileri" color="primary" size="small" />
              </Divider>

              <TextField
                label="Merchant ID"
                value={merchantId}
                onChange={(e) => setMerchantId(e.target.value)}
                fullWidth
                size="small"
                required
                autoComplete="off"
              />

              <TextField
                label="Merchant Key"
                value={merchantKey}
                onChange={(e) => setMerchantKey(e.target.value)}
                fullWidth
                size="small"
                required
                autoComplete="new-password"
                type={showMerchantKey ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowMerchantKey((v) => !v)}
                        edge="end"
                      >
                        {showMerchantKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Merchant Salt"
                value={merchantSalt}
                onChange={(e) => setMerchantSalt(e.target.value)}
                fullWidth
                size="small"
                required
                autoComplete="new-password"
                type={showMerchantSalt ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowMerchantSalt((v) => !v)}
                        edge="end"
                      >
                        {showMerchantSalt ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Alert severity="info" icon={false} sx={{ fontSize: '0.85rem' }}>
                Bu bilgileri PayTR Mağaza Panelinden <strong>Mağaza Bilgileri &gt; Entegrasyon Bilgileri</strong> bölümünden alabilirsiniz.
              </Alert>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          İptal
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} /> : undefined}
        >
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPaymentIntegrationModal;
